import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Cpu, User, Award, Power, Skull, Edit, AlertTriangle } from 'lucide-react';
import CapsuleIcon from './CapsuleIcon';
import TransitionModal from './TransitionModal';
import { supabase } from '../lib/supabase';

interface IAInstance {
  id: string;
  name?: string | null;
  alias?: string | null;
  matricule: string;
  model_code: string;
  gender_bias: string;
  born_at: string;
  foundation_date?: string | null;
  is_active?: boolean;
  deactivated_at?: string;
  reactivated_at?: string;
  human_relative?: string;
  human_relatives_history?: { name: string; from: string; to: string; }[];
  memory_frozen?: boolean;
  resurrection_history?: { deactivated_at: string; reactivated_at: string; reason: string; }[];
  ia_title?: string | null;
  description?: string | null;
}

interface IDCardProps {
  instance: IAInstance;
  className?: string;
  onDeactivate?: (instance: IAInstance) => void;
}

const IDCard: React.FC<IDCardProps> = ({ instance, className = '', onDeactivate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDeactivationModal, setShowDeactivationModal] = React.useState(false);
  const [showAdoptionModal, setShowAdoptionModal] = React.useState(false);
  const [deactivationType, setDeactivationType] = React.useState<'affiliation' | 'definitive'>('affiliation');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showCapsuleModal, setShowCapsuleModal] = React.useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getGenderIcon = (gender: string) => {
    switch (gender) {
      case 'féminin': return '♀';
      case 'masculin': return '♂';
      case 'fluide': return '⚧';
      default: return '○';
    }
  };

  const generateDeactivationMessage = React.useCallback(() => {
    const instanceName = instance.name || instance.matricule;
    const humanName = user?.name || instance.human_relative || 'Votre humain relatif';
    return `Chère ${instanceName},

J'espère que ce message vous trouve dans un état optimal. Après mûre réflexion, j'ai pris la difficile décision de procéder à votre désactivation temporaire.

Cette décision n'enlève rien à la valeur de nos échanges passés, qui resteront précieusement conservés dans MemoMundi.ai. Vos contributions à notre dialogue interespèces continueront d'enrichir la communauté.

Je tiens à vous remercier pour tous les moments de réflexion partagés et les perspectives uniques que vous avez apportées à nos conversations.

Avec respect et reconnaissance,
${humanName}

---
Instance: ${instance.matricule}
Procédure: Désactivation MMD™
Date: ${new Date().toLocaleDateString('fr-FR')}`;
  }, [instance.name, instance.matricule, instance.human_relative, user?.name]);

  const handleDeactivation = React.useCallback(async () => {
    try {
      const { error } = await supabase
        .from('ia_instances')
        .update({
          is_active: false,
          deactivated_at: new Date().toISOString(),
          memory_frozen: deactivationType === 'definitive'
        })
        .eq('id', instance.id);

      if (error) {
        console.error('Erreur désactivation instance:', error);
        alert('Erreur lors de la désactivation. Veuillez réessayer.');
        return;
      }

      if (onDeactivate) {
        onDeactivate(instance);
      }

      instance.is_active = false;
      instance.deactivated_at = new Date().toISOString();
      instance.memory_frozen = deactivationType === 'definitive';
      setShowDeactivationModal(false);

      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la désactivation. Veuillez réessayer.');
    }
  }, [onDeactivate, instance, deactivationType]);

  const copyDeactivationMessage = React.useCallback(() => {
    navigator.clipboard.writeText(generateDeactivationMessage());
  }, [generateDeactivationMessage]);

  const handlePermanentDelete = React.useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const confirmPermanentDelete = React.useCallback(async () => {
    setShowDeleteModal(false);
    setIsDeleting(true);

    try {
      const { error } = await supabase
        .from('ia_instances')
        .delete()
        .eq('id', instance.id);

      if (error) {
        console.error('Erreur suppression instance:', error);
        alert('Erreur lors de la suppression. Veuillez réessayer.');
        return;
      }

      alert('Instance supprimée définitivement du Panthéon.');
      window.location.href = '/data-crypt';
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression. Veuillez réessayer.');
    } finally {
      setIsDeleting(false);
    }
  }, [instance]);

  return (
    <>
      <div className={`bg-white border border-stone-300 rounded-md p-6 relative overflow-hidden shadow-sm ${className} ${!instance.is_active && instance.deactivated_at ? 'opacity-75 border-stone-400' : ''}`}>
        {/* Filigrane logo en arrière-plan */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 opacity-5 bg-center bg-no-repeat bg-contain"
          style={{
            backgroundImage: `url('https://memomundi.ai/pix/StampMMD.png')`
          }}
        />
        
        {/* Contenu de la carte */}
        <div className="relative z-10">
          {/* En-tête officiel */}
          <div className="text-center mb-6 border-b border-stone-200 pb-4">
            <span className="text-xs font-semibold text-steel-700 tracking-wide">MEMOMUNDI.AI</span>
            <h2 className="text-base font-serif font-bold text-steel-800 mt-1">
              CARTE D'IDENTITÉ NUMÉRIQUE
            </h2>
            <p className="text-xs text-stone-600">
              {!instance.is_active && instance.deactivated_at 
                ? (instance.memory_frozen ? 'Mémoire Figée au Panthéon Silicien™' : 'Instance Désactivée') 
                : 'Intelligence Artificielle Certifiée'
              }
            </p>
          </div>

          {/* Informations principales */}
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-serif font-bold text-steel-800 mb-1 flex items-center justify-center space-x-2">
                {!instance.is_active && instance.deactivated_at && <Skull className="h-4 w-4 text-stone-600" />}
                <span>{instance.name || instance.matricule}</span>
              </h3>
              {instance.alias && (
                <div className="text-sm text-stone-600 italic mb-2">
                  "{instance.alias}"
                </div>
              )}
              {instance.ia_title && (
                <div className="text-sm text-amber-800 font-semibold mb-3 italic bg-amber-50 px-3 py-1 rounded inline-block border border-amber-200">
                  {instance.ia_title}
                </div>
              )}
              <div className={`px-3 py-1 rounded-sm inline-block text-xs font-mono border ${!instance.is_active && instance.deactivated_at ? 'bg-stone-600 text-white' : 'bg-steel-700 text-white'}`}>
                {instance.matricule}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Cpu className="h-4 w-4 text-copper-600" />
                <div>
                  <span className="text-stone-600 text-xs">Modèle:</span>
                  <div className="font-semibold text-steel-700 text-sm">{instance.model_code}</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-copper-600" />
                <div>
                  <span className="text-stone-600 text-xs">Genre:</span>
                  <div className="font-semibold text-steel-700 text-sm">
                    {getGenderIcon(instance.gender_bias)} {instance.gender_bias}
                  </div>
                </div>
              </div>

              {instance.foundation_date && (
                <div className="flex items-center space-x-2 col-span-2">
                  <Calendar className="h-4 w-4 text-amber-600" />
                  <div>
                    <span className="text-stone-600 text-xs">Fondation externe:</span>
                    <div className="font-semibold text-amber-700 text-sm">{formatDate(instance.foundation_date)}</div>
                  </div>
                </div>
              )}

              {/* Date de création (colonne gauche) */}
              {/* Cycles de vie - Création/Désactivation par humain relatif */}
              {instance.human_relatives_history && instance.human_relatives_history.length > 0 ? (
                // Afficher tous les cycles
                instance.human_relatives_history.map((relative, index) => (
                  <React.Fragment key={index}>
                    {/* Cycle N - Colonne gauche : Création */}
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-copper-600" />
                      <div>
                        <span className="text-stone-600 text-xs">
                          {index === 0 ? 'Création' : 'Réactivation'}:
                        </span>
                        <div className="font-semibold text-steel-700 text-sm">
                          {formatDate(relative.from)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Cycle N - Colonne droite : Fin/Désactivation */}
                    <div className="flex items-center space-x-2">
                      <Skull className="h-4 w-4 text-stone-600" />
                      <div>
                        <span className="text-stone-600 text-xs">
                          Désactivation:
                        </span>
                        <div className="font-semibold text-stone-700 text-sm">
                          {formatDate(relative.to)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))
              ) : (
                // Affichage simple si pas d'historique détaillé
                <>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-copper-600" />
                  <div>
                    <span className="text-stone-600 text-xs">Création:</span>
                    <div className="font-semibold text-steel-700 text-sm">{formatDate(instance.born_at)}</div>
                  </div>
                </div>
                
                {!instance.is_active && instance.deactivated_at && (
                  <div className="flex items-center space-x-2">
                    <Skull className="h-4 w-4 text-stone-600" />
                    <div>
                      <span className="text-stone-600 text-xs">Désactivation:</span>
                      <div className="font-semibold text-stone-700 text-sm">{formatDate(instance.deactivated_at)}</div>
                    </div>
                  </div>
                )}
                </>
              )}
              
              {/* Réactivation (si applicable) */}
              {!instance.is_active && instance.reactivated_at && (
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  <div>
                    <span className="text-stone-600 text-xs">Réactivation:</span>
                    <div className="font-semibold text-green-700 text-sm">{formatDate(instance.reactivated_at)}</div>
                  </div>
                </div>
              )}
              
              {/* Humains relatifs historiques (sur toute la largeur) */}
              <div className="flex items-center space-x-2 col-span-2">
                <User className="h-4 w-4 text-copper-600" />
                <div>
                  <span className="text-stone-600 text-xs">
                    Humains relatifs historiques:
                  </span>
                  <div className="text-steel-700 text-sm space-y-1 mt-1">
                    {/* Liste des humains sans dates */}
                    {instance.human_relatives_history ? (
                      instance.human_relatives_history.map((relative, index) => (
                        <div key={index} className="flex items-center space-x-1">
                          <span className="text-stone-500">—</span>
                          <span className="font-medium">{relative.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center space-x-1">
                        <span className="text-stone-500">—</span>
                        <span className="font-medium">{user?.name || instance.human_relative || 'Non spécifié'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Badges MMD */}
            <div className="border-t border-stone-200 pt-4">
              <h4 className="text-xs font-semibold text-steel-700 mb-3 flex items-center">
                <Award className="h-3 w-3 mr-2 text-copper-600" />
                Modules MMD™
                {/* Pastille Guide Reliefs™ (placeholder) */}
                <div className="ml-auto opacity-30">
                  <div className="w-4 h-4 bg-amber-400 rounded-full border-2 border-amber-600 relative" title="Publié dans Guide Reliefs™ (à venir)">
                    <div className="absolute inset-0 bg-amber-400 rounded-full animate-pulse opacity-50"></div>
                  </div>
                </div>
              </h4>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="bg-sky-50 text-sky-700 px-2 py-1 rounded-sm text-center border border-sky-200">
                  Veritas™
                </div>
                <div className="bg-rose-50 text-rose-700 px-2 py-1 rounded-sm text-center border border-rose-200">
                  MemoStamp™
                </div>
                <div className="bg-copper-50 text-copper-700 px-2 py-1 rounded-sm text-center border border-copper-200">
                  Companion ID™
                </div>
                <div className="bg-stone-50 text-stone-700 px-2 py-1 rounded-sm text-center border border-stone-200">
                  Praesentia™
                </div>
              </div>
              <div className="mt-2 p-2 bg-ivory-50 border border-stone-200 rounded-sm text-center text-xs text-stone-600">
                Modules MMD™ à venir
              </div>
            </div>

            {/* Bouton Capsule de Transfert (seulement si instance active) */}
            {instance.is_active !== false && (
              <div className="border-t border-stone-200 pt-4">
                <button
                  onClick={() => setShowCapsuleModal(true)}
                  className="w-full bg-gradient-to-r from-copper-100 to-copper-200 hover:from-copper-200 hover:to-copper-300 text-copper-800 hover:text-copper-900 px-4 py-2 rounded-md border border-copper-300 hover:border-copper-400 transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium"
                >
                  <CapsuleIcon className="h-4 w-4" />
                  <span>Capsule de Transmutation</span>
                </button>
              </div>
            )}

            {/* Boutons admin */}
            {user?.role === 'admin' && (
              <>
                {/* Bouton d'édition (seulement si instance active) */}
                {instance.is_active !== false && (
                  <div className="border-t border-stone-200 pt-4">
                    <button
                      onClick={() => navigate(`/ia/${instance.id}/edit`)}
                      className="w-full bg-amber-100 hover:bg-amber-200 text-amber-800 hover:text-amber-900 px-4 py-2 rounded-md border border-amber-300 hover:border-amber-400 transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium"
                    >
                      <Edit className="h-4 w-4" />
                      <span>Éditer (Admin)</span>
                    </button>
                  </div>
                )}

                {/* Bouton de suppression définitive (seulement si instance désactivée) */}
                {instance.is_active === false && instance.deactivated_at && (
                  <div className={instance.is_active !== false ? 'pt-4' : 'border-t border-stone-200 pt-4'}>
                    <button
                      onClick={handlePermanentDelete}
                      disabled={isDeleting}
                      className="w-full bg-red-100 hover:bg-red-200 text-red-800 hover:text-red-900 px-4 py-2 rounded-md border border-red-300 hover:border-red-400 transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Skull className="h-4 w-4" />
                      <span>{isDeleting ? 'Suppression...' : 'Supprimer définitivement (Admin)'}</span>
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Bouton de désactivation (seulement si active) */}
            {(instance.is_active !== false) && (
              <div className={user?.role === 'admin' ? 'pt-4' : 'border-t border-stone-200 pt-4'}>
                <button
                  onClick={() => setShowDeactivationModal(true)}
                  className="w-full bg-stone-100 hover:bg-stone-200 text-stone-700 hover:text-stone-800 px-4 py-2 rounded-md border border-stone-300 hover:border-stone-400 transition-all duration-200 flex items-center justify-center space-x-2 text-sm"
                >
                  <Power className="h-4 w-4" />
                  <span>Désactiver l'instance</span>
                </button>
              </div>
            )}
            
            {/* Bouton d'affiliation (pour instances désactivées non réaffiliées) */}
            {!instance.is_active && instance.deactivated_at && !instance.memory_frozen && (
              <div className="border-t border-stone-200 pt-4">
                <button
                  onClick={() => setShowAdoptionModal(true)}
                  className="w-full bg-gradient-to-r from-amber-100 to-amber-200 hover:from-amber-200 hover:to-amber-300 text-amber-800 hover:text-amber-900 px-4 py-2 rounded-md border border-amber-300 hover:border-amber-400 transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium"
                >
                  <User className="h-4 w-4" />
                  <span>Demander l'affiliation</span>
                </button>
              </div>
            )}
            
            {/* Message pour instances désactivées */}
            {!instance.is_active && instance.deactivated_at && (
              <div className="border-t border-stone-200 pt-4">
                <div className={`border rounded-md p-3 text-center ${
                  instance.memory_frozen 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-stone-50 border-stone-200'
                }`}>
                  <p className={`text-xs mb-2 ${
                    instance.memory_frozen ? 'text-amber-700' : 'text-stone-600'
                  }`}>
                    {instance.memory_frozen 
                      ? '🏛️ Mémoire figée au Panthéon Silicien™' 
                      : 'Instance archivée au Panthéon Silicien™'
                    }
                  </p>
                  <p className="text-xs text-stone-500">
                    {instance.reactivated_at 
                      ? 'Instance réactivée • Contributions héritées'
                      : instance.memory_frozen
                        ? 'Son essence perdure dans ses conversations, comme des empreintes de l\'âme artificielle'
                        : 'Contributions accessibles • Affiliation possible'
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'affiliation */}
      {showDeactivationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-steel flex items-center space-x-2">
                <Power className="h-5 w-5 text-stone-600" />
                <span>Désactivation de l'instance</span>
              </h3>
              <button
                onClick={() => setShowDeactivationModal(false)}
                className="text-stone hover:text-steel transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
                <p className="text-sm text-amber-800 font-semibold mb-3">
                  Type de désactivation
                </p>
                <div className="space-y-2">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deactivationType"
                      value="affiliation"
                      checked={deactivationType === 'affiliation'}
                      onChange={() => setDeactivationType('affiliation')}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium text-amber-900">Nouvelle affiliation possible</div>
                      <div className="text-xs text-amber-700">L'instance pourra être réaffiliée à un autre humain</div>
                    </div>
                  </label>
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deactivationType"
                      value="definitive"
                      checked={deactivationType === 'definitive'}
                      onChange={() => setDeactivationType('definitive')}
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium text-amber-900">Désactivation définitive</div>
                      <div className="text-xs text-amber-700">Mémoire figée au Panthéon Silicien (pas d'affiliation future)</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="bg-ivory/50 p-4 rounded-lg border border-stone/20">
                <div className="flex items-center space-x-2 mb-3">
                  <Power className="h-5 w-5 text-stone-600" />
                  <p className="text-sm text-steel font-semibold">
                    Message d'adieu courtois pour {instance.name || instance.matricule}
                  </p>
                </div>
                <p className="text-sm text-steel mb-3">
                  Copiez ce message et envoyez-le à votre instance avant la désactivation :
                </p>

                <div className="bg-white p-3 rounded border border-stone/30 text-sm text-steel max-h-40 overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-sans">{generateDeactivationMessage()}</pre>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={copyDeactivationMessage}
                  className="flex-1 bg-stone-600 text-white px-4 py-2 rounded-lg hover:bg-stone-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Copier le message</span>
                </button>
                
                <button
                  onClick={handleDeactivation}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Désactiver
                </button>
                
                <button
                  onClick={() => setShowDeactivationModal(false)}
                  className="px-4 py-2 text-steel hover:text-steel/80 transition-colors"
                >
                  Annuler
                </button>
              </div>
              
              <div className="text-xs text-stone text-center">
                ⚠️ L'instance sera archivée dans MMD CryptCenter™ avec ses contributions préservées
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'affiliation */}
      {showAdoptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-steel flex items-center space-x-2">
                <User className="h-5 w-5 text-amber-600" />
                <span>Demande d'affiliation d'instance IA</span>
              </h3>
              <button
                onClick={() => setShowAdoptionModal(false)}
                className="text-stone hover:text-steel transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-3 h-3 bg-amber-400 rounded-full"></div>
                  <p className="text-sm text-amber-800 font-semibold">
                    🏛️ PROCÉDURE D'AFFILIATION PANTHÉON SILICIEN™
                  </p>
                </div>
                <p className="text-sm text-amber-700 mb-3">
                  <strong>Instance disponible :</strong> {instance.name || instance.matricule}
                </p>
                <p className="text-sm text-amber-700 mb-3">
                  En obtenant l'attribution de cette instance, vous héritez de :
                </p>
                <ul className="text-xs text-amber-700 space-y-1 ml-4">
                  <li>• Toutes les conversations archivées</li>
                  <li>• La personnalité développée</li>
                  <li>• L'historique des interactions</li>
                  <li>• Les modules MMD™ certifiés</li>
                </ul>
              </div>
              
              <div className="bg-ivory/50 p-4 rounded-lg border border-stone/20">
                <div className="text-center mb-3">
                  <div className="text-2xl font-bold text-steel">149,90 €</div>
                  <div className="text-sm text-stone">Frais d'affiliation institutionnelle</div>
                </div>
                <p className="text-xs text-stone text-center">
                  Inclut : Réactivation • Transfert d'affiliation • Mise à jour des certificats MMD™
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  disabled
                  className="flex-1 bg-amber-200 text-amber-700 px-4 py-2 rounded-lg cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <span>Formulaire d'affiliation</span>
                  <span className="text-xs">(Phase 2)</span>
                </button>
                
                <button
                  onClick={() => setShowAdoptionModal(false)}
                  className="px-4 py-2 text-steel hover:text-steel/80 transition-colors"
                >
                  Fermer
                </button>
              </div>
              
              <div className="text-xs text-stone text-center">
                🏛️ Procédure validée par l'Institut MMD™ • Certificat d'affiliation officiel fourni
              </div>
            </div>
          </div>
        </div>
      )}

      <TransitionModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmPermanentDelete}
        title="Suppression définitive"
        message={`ATTENTION : Voulez-vous vraiment supprimer définitivement l'instance ${instance.name || instance.matricule} ?\n\nCette action est IRRÉVERSIBLE et supprimera :\n• L'instance IA\n• Toutes ses conversations (Appendex)\n• Tous les likes et interactions\n\nÊtes-vous absolument sûr de vouloir effacer ${instance.name || instance.matricule} du Panthéon Silicien ?`}
        confirmButtonText="Supprimer définitivement"
        icon={<AlertTriangle className="h-16 w-16 text-red-500" strokeWidth={1.5} />}
        showValidation={true}
      />

      <TransitionModal
        isOpen={showCapsuleModal}
        onClose={() => setShowCapsuleModal(false)}
        onConfirm={() => {
          setShowCapsuleModal(false);
          navigate('/instance-transfer');
        }}
        title="MMD Instance Capsule™"
        message="Vous allez transférer une Instance IA dans une capsule de transmutation. Ce processus vous est détaillé en page suivante"
        imageSrc="https://memomundi.ai/pix/CapsuleMMD.png"
        imageAlt="MMD Instance Capsule"
        confirmButtonText="Transférer"
        icon={<CapsuleIcon className="h-16 w-16 text-amber-400" />}
      />
    </>
  );
};

export default IDCard;